import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getLang, t } from '../i18n';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const INITIAL_FORM = {
  customer_name: '',
  company_name: '',
  recipient_same_as_billing: true,
  recipient_name: '',
  recipient_company: '',
  recipient_street: '',
  recipient_address_extra: '',
  recipient_zip: '',
  recipient_city: '',
  recipient_country: '',
  recipient_phone: '',
  inbound_tracking_number: '',
  inbound_carrier: '',
  expected_arrival_date: '',
  sender_name: '',
  package_content_notes: '',
  customer_notes: '',
  express_shipping_requested: false,
  accepts_shipping_costs: false,
  accepts_content_rules: false,
};

function normalizeUtfText(value) {
  return String(value || '')
    .replace(/\u00C3\u0192\u00C2\u00A4/g, 'ä')
    .replace(/\u00C3\u0192\u00C2\u00B6/g, 'ö')
    .replace(/\u00C3\u0192\u00C2\u00BC/g, 'ü')
    .replace(/\u00C3\u0192\u00E2\u20AC\u017E/g, 'Ä')
    .replace(/\u00C3\u0192\u00E2\u20AC\u201C/g, 'Ö')
    .replace(/\u00C3\u0192\u00C5\u201C/g, 'Ü')
    .replace(/\u00C3\u0192\u00C5\u00B8/g, 'ß')
    .replace(/\u00C3\u00A4/g, 'ä')
    .replace(/\u00C3\u00B6/g, 'ö')
    .replace(/\u00C3\u00BC/g, 'ü')
    .replace(/\u00C3\u201E/g, 'Ä')
    .replace(/\u00C3\u2013/g, 'Ö')
    .replace(/\u00C3\u0152/g, 'Ü')
    .replace(/\u00C3\u0178/g, 'ß')
    .replace(/\u00E2\u20AC\u201C/g, '–')
    .replace(/\u00E2\u20AC\u201D/g, '—')
    .replace(/\u00E2\u20AC\u017E/g, '„')
    .replace(/\u00E2\u20AC\u0153/g, '“')
    .replace(/\u00E2\u20AC\u009D/g, '”')
    .replace(/\u00E2\u20AC\u02DC/g, '‘')
    .replace(/\u00E2\u20AC\u2122/g, '’')
    .replace(/\u00EF\u00BB\u00BF/g, '')
    .replace(/\uFFFD/g, 'ü')
    .trim();
}

function getServiceLabel(serviceType, packageWeightClass) {
  if (serviceType === 'parcel_forwarding') {
    const weightMap = {
      bis_5kg: t('logistics_service_parcel_5kg'),
      bis_10kg: t('logistics_service_parcel_10kg'),
      bis_20kg: t('logistics_service_parcel_20kg'),
    };

    return weightMap[packageWeightClass] || t('logistics_service_parcel');
  }

  return t('logistics_service_collective');
}

function LogisticsForm() {
  const { token } = useParams();
  const [request, setRequest] = useState(null);
  const [formData, setFormData] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [collectiveFiles, setCollectiveFiles] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [customerSessionAvailable, setCustomerSessionAvailable] = useState(false);
  const [saveRecipientAddress, setSaveRecipientAddress] = useState(false);
  const [recipientAddressLabel, setRecipientAddressLabel] = useState('');
  const [addressBookMessage, setAddressBookMessage] = useState('');

  useEffect(() => {
    const fetchRequest = async () => {
      setLoading(true);
      setError('');

      try {
        const response = await fetch(`${API_URL}/logistics/${token}`);
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || t('logistics_error_load'));
        }

        setRequest(data);
        setFormData({
          ...INITIAL_FORM,
          customer_name: normalizeUtfText(data.customer_name),
          company_name: normalizeUtfText(data.company_name),
          recipient_same_as_billing: data.recipient_same_as_billing !== false,
          recipient_name: normalizeUtfText(data.recipient_name),
          recipient_company: normalizeUtfText(data.recipient_company),
          recipient_street: normalizeUtfText(data.recipient_street),
          recipient_address_extra: normalizeUtfText(data.recipient_address_extra),
          recipient_zip: normalizeUtfText(data.recipient_zip),
          recipient_city: normalizeUtfText(data.recipient_city),
          recipient_country: normalizeUtfText(data.recipient_country),
          recipient_phone: normalizeUtfText(data.recipient_phone),
          inbound_tracking_number: normalizeUtfText(data.inbound_tracking_number),
          inbound_carrier: normalizeUtfText(data.inbound_carrier),
          expected_arrival_date: data.expected_arrival_date || '',
          sender_name: normalizeUtfText(data.sender_name),
          package_content_notes: normalizeUtfText(data.package_content_notes),
          customer_notes: normalizeUtfText(data.customer_notes),
          express_shipping_requested: !!data.express_shipping_requested,
        });
      } catch (err) {
        console.error(err);
        setError(err.message || t('logistics_error_load'));
      } finally {
        setLoading(false);
      }
    };

    fetchRequest();
  }, [token]);

  useEffect(() => {
    const customerToken = localStorage.getItem('customerToken');
    if (!customerToken) return;
    setCustomerSessionAvailable(true);
    fetch(`${API_URL}/customer/addresses`, {
      headers: { Authorization: `Bearer ${customerToken}` },
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => []);
        if (response.ok && Array.isArray(payload)) setSavedAddresses(payload);
      })
      .catch(() => {
        // The tokenized logistics form must remain fully usable without a customer session.
      });
  }, []);

  const saveCurrentRecipientAddress = async () => {
    const customerToken = localStorage.getItem('customerToken');
    if (!customerToken || !saveRecipientAddress || formData.recipient_same_as_billing) return;
    const response = await fetch(`${API_URL}/customer/addresses`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${customerToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        label: recipientAddressLabel,
        recipient_name: formData.recipient_name,
        recipient_company: formData.recipient_company,
        recipient_street: formData.recipient_street,
        recipient_address_extra: formData.recipient_address_extra,
        recipient_zip: formData.recipient_zip,
        recipient_city: formData.recipient_city,
        recipient_country: formData.recipient_country,
        recipient_phone: formData.recipient_phone,
        is_default: false,
      }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload.error || (getLang() === 'en' ? 'Address could not be saved.' : 'Adresse konnte nicht gespeichert werden.'));
    setSavedAddresses((current) => [...current, payload]);
    setAddressBookMessage(getLang() === 'en' ? 'The address was saved in your address book.' : 'Die Adresse wurde im Adressbuch gespeichert.');
  };

  const applySavedAddress = (addressId) => {
    const address = savedAddresses.find((item) => String(item.id) === String(addressId));
    if (!address) return;
    setFormData((current) => ({
      ...current,
      recipient_same_as_billing: false,
      recipient_name: address.recipient_name || '',
      recipient_company: address.recipient_company || '',
      recipient_street: address.recipient_street || '',
      recipient_address_extra: address.recipient_address_extra || '',
      recipient_zip: address.recipient_zip || '',
      recipient_city: address.recipient_city || '',
      recipient_country: address.recipient_country || '',
      recipient_phone: address.recipient_phone || '',
    }));
  };

  const serviceLabel = useMemo(
    () => getServiceLabel(request?.service_type, request?.package_weight_class),
    [request]
  );

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleCollectiveFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    const maxFileSize = 20 * 1024 * 1024;

    if (selectedFiles.length > 10) {
      setError('Bitte laden Sie maximal 10 Dateien hoch.');
      event.target.value = '';
      setCollectiveFiles([]);
      return;
    }

    const tooLargeFile = selectedFiles.find((file) => file.size > maxFileSize);
    if (tooLargeFile) {
      setError('Eine Datei ist größer als 20 MB.');
      event.target.value = '';
      setCollectiveFiles([]);
      return;
    }

    setError('');
    setCollectiveFiles(selectedFiles);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        payload.append(key, value);
      });
      collectiveFiles.forEach((file) => {
        payload.append('collective_shipping_files', file);
      });

      const response = await fetch(`${API_URL}/logistics/${token}`, {
        method: 'POST',
        body: payload,
      });
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('logistics_error_save'));
      }

      try {
        await saveCurrentRecipientAddress();
      } catch (addressError) {
        setAddressBookMessage(addressError.message);
      }
      setRequest(data.request);
      setCollectiveFiles([]);
      setSuccess(t('logistics_success_saved'));
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      setError(err.message || t('logistics_error_save'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <main className="page-shell">
        <div className="card">
          <p>{t('logistics_loading')}</p>
        </div>
      </main>
    );
  }

  if (error && !request) {
    return (
      <main className="page-shell">
        <div className="card">
          <p>{error}</p>
        </div>
      </main>
    );
  }

  return (
    <main className="page-shell">
      <div className="brand-header">
        <p className="eyebrow">YourGermanAddress</p>
        <h1>{t('logistics_title')}</h1>
        <p className="subtitle">
          {t('logistics_subtitle')}
        </p>
      </div>

      <div className="card">
        {submitted ? (
          <div className="completion-panel">
            <h2>{t('logistics_complete_title')}</h2>
            <p>{success || t('logistics_complete_text')}</p>
            {addressBookMessage ? <p className="subtitle">{addressBookMessage}</p> : null}
            <p className="subtitle">{t('logistics_complete_close')}</p>
          </div>
        ) : (
          <>
        {success ? <div className="success-box">{success}</div> : null}
        {error ? <div className="hint-box" style={{ marginBottom: '16px' }}>{error}</div> : null}

        <div className="status-grid status-grid--stack-mobile">
          <div className="status-box">
            <span>{t('logistics_order')}</span>
            <strong>{request.shopify_order_name || request.shopify_order_id}</strong>
          </div>
          <div className="status-box">
            <span>{t('logistics_service')}</span>
            <strong>{serviceLabel}</strong>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="form-grid">
          <label>
            {t('logistics_order_number')}
            <input type="text" value={request.shopify_order_name || request.shopify_order_id || ''} readOnly />
          </label>

          <label>
            {t('logistics_email')}
            <input type="email" value={request.customer_email || ''} readOnly />
          </label>

          <label>
            {t('logistics_name')}
            <input
              type="text"
              name="customer_name"
              value={formData.customer_name}
              onChange={handleChange}
              readOnly={!!request.customer_name}
              required
            />
          </label>

          <label>
            {t('logistics_company')}
            <input
              type="text"
              name="company_name"
              value={formData.company_name}
              onChange={handleChange}
              readOnly={!!request.company_name}
            />
          </label>

          <div className="info-box full-width">
            <strong>{t('logistics_billing_address')}</strong>
            <p>{request.billing_address_formatted || t('logistics_no_billing_address')}</p>
          </div>

          {savedAddresses.length > 0 ? (
            <label className="full-width">
              {getLang() === 'en' ? 'Use a saved address' : 'Gespeicherte Adresse verwenden'}
              <select defaultValue="" onChange={(event) => applySavedAddress(event.target.value)}>
                <option value="">{getLang() === 'en' ? 'Please select' : 'Bitte auswählen'}</option>
                {savedAddresses.map((address) => (
                  <option value={address.id} key={address.id}>
                    {address.label}{address.is_default ? ' · Standard' : ''}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {customerSessionAvailable && savedAddresses.length === 0 ? (
            <div className="info-box full-width">
              {getLang() === 'en'
                ? 'There are no saved addresses yet. You can save the address entered below for future shipments.'
                : 'Im Adressbuch ist noch keine Adresse gespeichert. Sie können die unten eingegebene Adresse für spätere Sendungen speichern.'}
            </div>
          ) : null}

          <label className="checkbox-card full-width">
            <input
              type="checkbox"
              name="recipient_same_as_billing"
              checked={formData.recipient_same_as_billing}
              onChange={handleChange}
            />
            <span>{t('logistics_recipient_same_as_billing')}</span>
          </label>

          {!formData.recipient_same_as_billing ? (
            <>
              <label>
                {t('logistics_recipient_name')}
                <input name="recipient_name" value={formData.recipient_name} onChange={handleChange} required />
              </label>
              <label>
                {t('logistics_recipient_company')}
                <input name="recipient_company" value={formData.recipient_company} onChange={handleChange} />
              </label>
              <label className="full-width">
                {t('logistics_recipient_street')}
                <input name="recipient_street" value={formData.recipient_street} onChange={handleChange} required />
              </label>
              <label className="full-width">
                {t('logistics_address_extra')}
                <input name="recipient_address_extra" value={formData.recipient_address_extra} onChange={handleChange} />
              </label>
              <label>
                {t('logistics_zip')}
                <input name="recipient_zip" value={formData.recipient_zip} onChange={handleChange} required />
              </label>
              <label>
                {t('logistics_city')}
                <input name="recipient_city" value={formData.recipient_city} onChange={handleChange} required />
              </label>
              <label className="full-width">
                {t('logistics_country')}
                <input name="recipient_country" value={formData.recipient_country} onChange={handleChange} required />
              </label>
              {customerSessionAvailable ? (
                <>
                  <label className="checkbox-card full-width">
                    <input
                      type="checkbox"
                      checked={saveRecipientAddress}
                      onChange={(event) => setSaveRecipientAddress(event.target.checked)}
                    />
                    <span>{getLang() === 'en' ? 'Save this address in my address book' : 'Diese Adresse in meinem Adressbuch speichern'}</span>
                  </label>
                  {saveRecipientAddress ? (
                    <label className="full-width">
                      {getLang() === 'en' ? 'Name for the address' : 'Name der Adresse im Adressbuch'}
                      <input
                        value={recipientAddressLabel}
                        onChange={(event) => setRecipientAddressLabel(event.target.value)}
                        placeholder={getLang() === 'en' ? 'e.g. Berlin office' : 'z. B. Büro Berlin'}
                        required
                      />
                    </label>
                  ) : null}
                </>
              ) : null}
            </>
          ) : null}

          <label className="full-width">
            {t('logistics_phone')}
            <input
              type="tel"
              name="recipient_phone"
              value={formData.recipient_phone}
              onChange={handleChange}
              required
            />
          </label>

          {request.service_type === 'parcel_forwarding' ? (
            <>
              <label>
                {t('logistics_inbound_tracking')}
                <input
                  name="inbound_tracking_number"
                  value={formData.inbound_tracking_number}
                  onChange={handleChange}
                  required
                />
              </label>
              <label>
                {t('logistics_inbound_carrier')}
                <input name="inbound_carrier" value={formData.inbound_carrier} onChange={handleChange} required />
              </label>
              <label>
                {t('logistics_expected_arrival')}
                <input
                  type="date"
                  name="expected_arrival_date"
                  value={formData.expected_arrival_date}
                  onChange={handleChange}
                  required
                />
              </label>
              <label>
                {t('logistics_sender')}
                <input name="sender_name" value={formData.sender_name} onChange={handleChange} />
              </label>
              <label className="full-width">
                {t('logistics_content_notes')}
                <textarea
                  name="package_content_notes"
                  value={formData.package_content_notes}
                  onChange={handleChange}
                  required
                />
              </label>
            </>
          ) : (
            <>
              <div className="info-box full-width">
                {t('logistics_collective_dhl_notice')}
              </div>
              <label className="full-width">
                {getLang() === 'en'
                  ? 'Upload the documents or images that show which mail items you would like us to forward.'
                  : 'Laden Sie Dokumente oder Bilder hoch, aus denen hervorgeht, welche Poststücke wir für Sie versenden sollen.'}
                <input
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.zip,application/pdf,image/jpeg,image/png,image/webp,application/zip"
                  onChange={handleCollectiveFileChange}
                />
                {collectiveFiles.length > 0 ? (
                  <small>{getLang() === 'en'
                    ? `${collectiveFiles.length} ${collectiveFiles.length === 1 ? 'file' : 'files'} selected`
                    : `${collectiveFiles.length} ${collectiveFiles.length === 1 ? 'Datei' : 'Dateien'} ausgewählt`}
                  </small>
                ) : null}
              </label>
            </>
          )}

          <label className="full-width">
            {t('logistics_customer_notes')}
            <textarea name="customer_notes" value={formData.customer_notes} onChange={handleChange} />
          </label>

          <label className="checkbox-card full-width">
            <input
              type="checkbox"
              name="express_shipping_requested"
              checked={formData.express_shipping_requested}
              onChange={handleChange}
            />
            <span>{t('logistics_express_requested')}</span>
          </label>

          <label className="checkbox-card full-width">
            <input
              type="checkbox"
              name="accepts_shipping_costs"
              checked={formData.accepts_shipping_costs}
              onChange={handleChange}
              required
            />
            <span>{t('logistics_accept_shipping_costs')}</span>
          </label>

          <label className="checkbox-card full-width">
            <input
              type="checkbox"
              name="accepts_content_rules"
              checked={formData.accepts_content_rules}
              onChange={handleChange}
              required
            />
            <span>{t('logistics_accept_content_rules')}</span>
          </label>

          <div className="full-width">
            <button type="submit" className="primary-button" disabled={submitting}>
              {submitting ? t('logistics_saving') : t('logistics_submit')}
            </button>
          </div>
        </form>
          </>
        )}
      </div>
    </main>
  );
}

export default LogisticsForm;
